/**
 * What the patients in view are on: drug, current dose level, and the
 * weight-loss goal they gave at intake. With a comparison group, each row shows
 * both groups' shares on one scale so the gap reads directly.
 *
 * Levels are per drug (Tirz L3 and Sema L3 are different doses), so they are
 * listed per drug and never pooled.
 */

const pct = v => (v == null ? '–' : `${(v * 100).toFixed(1)}%`)
const count = v => Number(v || 0).toLocaleString('en-US')

const SECTIONS = [
  { key: 'drug', title: 'Drug' },
  { key: 'level', title: 'Current dose level' },
  { key: 'goal', title: 'Weight-loss goal' },
]

function Section({ title, a, b, aTotal, bTotal }) {
  const values = [...a]
  // A value only the comparison group has still gets a row.
  for (const row of b || []) if (!values.some(v => v.value === row.value)) values.push({ ...row, patients: 0 })
  const bBy = new Map((b || []).map(r => [r.value, r]))
  const max = Math.max(
    0.0001,
    ...values.map(v => v.patients / (aTotal || 1)),
    ...(b ? b.map(v => v.patients / (bTotal || 1)) : []),
  )

  return (
    <div className="min-w-0">
      <h3 className="mb-2 text-sm font-medium">{title}</h3>
      <table className="w-full text-sm tabular-nums">
        <tbody>
          {values.map(row => {
            const aShare = aTotal ? row.patients / aTotal : null
            const bRow = bBy.get(row.value)
            const bShare = b && bTotal ? (bRow?.patients || 0) / bTotal : null
            return (
              <tr key={row.value} className="border-b last:border-0 align-top">
                <td className="w-24 py-1.5 pr-2">{row.label}</td>
                <td className="py-1.5 pr-2">
                  <div className="flex flex-col gap-1 pt-1">
                    <div className="h-2 rounded-sm bg-foreground/60" style={{ width: `${((aShare || 0) / max) * 100}%` }} />
                    {b && <div className="h-2 rounded-sm bg-foreground/20" style={{ width: `${((bShare || 0) / max) * 100}%` }} />}
                  </div>
                </td>
                <td className="w-24 py-1.5 text-right">
                  <div>{count(row.patients)} · {pct(aShare)}</div>
                  {b && <div className="text-muted-foreground">{count(bRow?.patients)} · {pct(bShare)}</div>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default function TreatmentMix({ groups }) {
  const [a, b] = groups
  return (
    <div className="space-y-3">
      {b && (
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-4 rounded-sm bg-foreground/60" />{a.label} (top number)</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-4 rounded-sm bg-foreground/20" />{b.label} (bottom number)</span>
        </div>
      )}
      <div className="grid gap-6 md:grid-cols-3">
        {SECTIONS.map(section => (
          <Section
            key={section.key}
            title={section.title}
            a={a.mix[section.key]}
            b={b?.mix[section.key]}
            aTotal={a.patients}
            bTotal={b?.patients}
          />
        ))}
      </div>
    </div>
  )
}
